import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// ==========================================================
// 상수
// ==========================================================

const GRID_COLOR = "#333333";
const X_TICKS    = [5, 10, 15, 20, 25, 30];

const TYPE_CONFIG = {
  elec:  { key: 'elec_usage',  label: '전기', color: '#f59e0b', unit: 'kWh', icon: '⚡' },
  gas:   { key: 'gas_usage',   label: '가스', color: '#ef4444', unit: 'm³',  icon: '🔥' },
  water: { key: 'water_usage', label: '수도', color: '#3b82f6', unit: 'm³',  icon: '💧' },
};

const METRIC_CONFIG = {
  elec:  { label: '전기', color: '#f59e0b', unit: 'kWh', icon: '⚡' },
  gas:   { label: '가스', color: '#ef4444', unit: 'm³',  icon: '🔥' },
  water: { label: '수도', color: '#3b82f6', unit: 'm³',  icon: '💧' },
};

// ==========================================================
// 개별 탭 차트 컴포넌트
// ==========================================================

const SingleChart = ({ typeKey, data, total }) => {
  const config     = TYPE_CONFIG[typeKey];
  const totalValue = total.toFixed(typeKey === 'water' ? 2 : 1);

  return (
    <div style={{ marginBottom: '40px', background: '#fff', border: `1px solid ${GRID_COLOR}`, padding: '15px' }}>
      <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '15px', color: config.color, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{config.icon} {config.label} 사용량</span>
        <span style={{ fontSize: '13px', background: '#f3f4f6', padding: '4px 10px', borderRadius: '5px', border: '1px solid #ddd' }}>
          총합: {totalValue} {config.unit}
        </span>
      </div>
      <div style={{ width: '100%', height: '220px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 40 }}>
            <CartesianGrid stroke={GRID_COLOR} strokeDasharray="0" vertical={true} horizontal={true} />
            <XAxis
              dataKey="date"
              type="number"
              domain={[1, 31]}
              ticks={X_TICKS}
              tick={{ fontSize: 12, fill: GRID_COLOR, fontWeight: 'bold' }}
              axisLine={{ stroke: GRID_COLOR }}
              tickLine={{ stroke: GRID_COLOR }}
              unit="일"
              dy={15}
            />
            <YAxis
              tick={{ fontSize: 11, fill: GRID_COLOR, fontWeight: 'bold' }}
              axisLine={{ stroke: GRID_COLOR }}
              width={45}
              tickFormatter={v => Number(v.toFixed(1))}
            />
            <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
            <Bar dataKey={config.key} fill={config.color} barSize={15} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ==========================================================
// 전체 탭 — 세대별 가로 막대그래프
// ==========================================================

function AllRoomsChart({ API_BASE, tenantId }) {
  const [month,  setMonth]  = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [metrics, setMetrics] = useState(['elec']);
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!tenantId || metrics.length === 0) return;
    const fetch_ = async () => {
      // 해당 조건의 캐시가 있으면 즉시 표시
      const cacheKey = `usage_all_${month}_${metrics.join('_')}`;
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (cached) setData(cached);
      } catch {}
      setLoading(true);
      try {
        // 선택된 metric 전부 병렬 호출
        const results = await Promise.all(
          metrics.map(m =>
            fetch(`${API_BASE}/api/usage/all-rooms?year_month=${month}&metric=${m}&tenant_id=${tenantId}`)
              .then(r => r.json())
              .then(json => ({ metric: m, data: json }))
          )
        );
        // room_no 기준으로 병합
        const roomMap = {};
        results.forEach(({ metric: m, data: rows }) => {
          (Array.isArray(rows) ? rows : []).forEach(r => {
            if (!roomMap[r.room_no]) roomMap[r.room_no] = { room_no: r.room_no };
            roomMap[r.room_no][m] = r.usage;
          });
        });
        const sorted = Object.values(roomMap)
          .sort((a, b) => a.room_no.localeCompare(b.room_no, undefined, { numeric: true }));
        setData(sorted);
        try { localStorage.setItem(`usage_all_${month}_${metrics.join('_')}`, JSON.stringify(sorted)); } catch {};
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [month, metrics, tenantId, API_BASE]);


  // 전기(kWh) / 가스+수도(m³) 축을 분리하고, 각자 최댓값을 보기 좋은 단위로 올림
  const elecMax = Math.max(...data.map(d => d.elec || 0), 0);
  const gwMax   = Math.max(...data.flatMap(d => [d.gas || 0, d.water || 0]), 0);
  const elecAxisMax = Math.max(Math.ceil(elecMax / 10) * 10, 10);
  const gwAxisMax   = Math.max(Math.ceil(gwMax / 5) * 5, 5);

  const hasElec = metrics.includes('elec');
  const hasGW   = metrics.includes('gas') || metrics.includes('water');

  // tickCount는 recharts가 "보기 좋은 숫자"로 임의 생성해 간격이 들쭉날쭉해짐 → 직접 5등분한 배열을 강제 지정
  const TICK_N = 5;
  const elecTicks = Array.from({ length: TICK_N }, (_, i) => Math.round(elecAxisMax * i / (TICK_N - 1)));
  const gwTicks   = Array.from({ length: TICK_N }, (_, i) => Math.round(gwAxisMax   * i / (TICK_N - 1)));

  // 차트 높이: 세대당 28px + 여백 (두 축일 때 하단 여백 추가)
  const chartHeight = Math.max(data.length * (metrics.length > 1 ? 36 : 28) + 60 + (hasElec && hasGW ? 20 : 0), 200);

  return (
    <div style={{ padding: '16px', fontFamily: 'sans-serif' }}>
      {/* 컨트롤 */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          style={{ padding: '8px', borderRadius: '8px', border: '1px solid #ccc', fontSize: '13px' }} />
        <div style={{ display: 'flex', gap: '6px' }}>
          {Object.entries(METRIC_CONFIG).map(([key, c]) => (
            <button key={key} onClick={() => setMetrics(prev =>
                prev.includes(key)
                  ? prev.length > 1 ? prev.filter(k => k !== key) : prev
                  : [...prev, key]
              )}
              style={{ padding: '8px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                       fontWeight: 'bold', fontSize: '12px',
                       backgroundColor: metrics.includes(key) ? c.color : '#e5e7eb',
                       color: metrics.includes(key) ? '#fff' : '#374151' }}>
              {c.icon} {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* 그래프 */}
      {loading && data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>로딩 중...</div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>데이터가 없습니다</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #e5e7eb' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold', fontSize: '14px', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
            <span>{month} 사용량 ({data.length}세대)</span>
            <span style={{ fontSize: '12px', background: '#f3f4f6', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e5e7eb', display: 'flex', gap: '10px' }}>
              {metrics.map(m => {
                const total = data.reduce((s, d) => s + (d[m] || 0), 0);
                const c = METRIC_CONFIG[m];
                return <span key={m} style={{ color: c.color, fontWeight: 'bold' }}>{c.icon} {total.toFixed(1)} {c.unit}</span>;
              })}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 0, right: 50, left: 30, bottom: (hasElec && hasGW) ? 20 : 0 }}
            >
              {/* 격자선: 두 축 모두 ticks 배열을 5등분으로 직접 지정, domain이 0부터 시작하므로 픽셀 위치가 일치 → 하나만 그려도 됨 */}
              <CartesianGrid strokeDasharray="3 3" horizontal={false} xAxisId={hasElec ? 'elec' : 'gw'} />
              {hasElec && (
                <XAxis
                  xAxisId="elec"
                  type="number"
                  domain={[0, elecAxisMax]}
                  tick={{ fontSize: 10, fill: '#374151' }}
                  ticks={elecTicks}
                  tickFormatter={v => `${Math.round(v)}kWh`}
                />
              )}
              {hasGW && (
                <XAxis
                  xAxisId="gw"
                  type="number"
                  domain={[0, gwAxisMax]}
                  tick={{ fontSize: 10, fill: '#374151' }}
                  ticks={gwTicks}
                  tickFormatter={v => `${Math.round(v)}m³`}
                  orientation="bottom"
                />
              )}
              <YAxis
                type="category"
                dataKey="room_no"
                tick={{ fontSize: 11, fill: '#374151', fontWeight: 'bold' }}
                width={36}
                tickFormatter={v => v.endsWith('호') ? v : `${v}호`}
              />
              <Tooltip
                formatter={(val, name) => {
                  const c = METRIC_CONFIG[name];
                  return [`${Number(val).toFixed(1)} ${c?.unit || ''}`, c?.label || name];
                }}
                labelFormatter={(label) => label.endsWith('호') ? label : `${label}호`}
              />
              {metrics.map(m => (
                <Bar key={m} dataKey={m} xAxisId={m === 'elec' ? 'elec' : 'gw'} fill={METRIC_CONFIG[m].color} barSize={metrics.length > 1 ? 10 : 16} radius={[0, 4, 4, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ==========================================================
// 개별 탭
// ==========================================================

function IndividualChart({ API_BASE, tenantId, roomList }) {
  const [room, setRoom]                   = useState("");
  const [month, setMonth]                 = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedTypes, setSelectedTypes] = useState(['elec', 'gas', 'water']);
  const [data, setData]                   = useState([]);
  const [loading, setLoading]             = useState(false);

  useEffect(() => {
    if (roomList?.length > 0 && !room) setRoom(roomList[0]);
  }, [roomList, room]);

  useEffect(() => {
    if (!tenantId || !room) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const url  = `${API_BASE}/api/usage/monthly/${encodeURIComponent(room)}?year_month=${month}&tenant_id=${tenantId}`;
        const res  = await fetch(url);
        const json = await res.json();
        setData(Array.isArray(json) ? json.map(item => ({ ...item, date: parseInt(item.date) })) : []);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchData();
  }, [room, month, tenantId, API_BASE]);

  const totals = useMemo(() =>
    Object.keys(TYPE_CONFIG).reduce((acc, key) => {
      acc[key] = data.reduce((sum, cur) => sum + (parseFloat(cur[TYPE_CONFIG[key].key]) || 0), 0);
      return acc;
    }, {}),
  [data]);

  const toggleType = (key) => {
    if (selectedTypes.includes(key)) {
      if (selectedTypes.length > 1) setSelectedTypes(selectedTypes.filter(k => k !== key));
    } else {
      setSelectedTypes([...selectedTypes, key]);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif', backgroundColor: '#f3f4f6', minHeight: '100vh' }}>
      <div style={{ marginBottom: '25px', display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <select value={room} onChange={e => setRoom(e.target.value)}
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }}>
            {roomList.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ccc' }} />
        </div>
        <div style={{ display: 'flex', gap: '15px', background: '#fff', padding: '10px 20px', borderRadius: '25px', border: '1px solid #ddd', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
          {Object.keys(TYPE_CONFIG).map(key => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold' }}>
              <input type="checkbox" checked={selectedTypes.includes(key)} onChange={() => toggleType(key)}
                style={{ width: '16px', height: '16px', accentColor: TYPE_CONFIG[key].color }} />
              <span style={{ color: TYPE_CONFIG[key].color }}>{TYPE_CONFIG[key].label}</span>
            </label>
          ))}
        </div>
      </div>
      {!loading && data.length > 0 ? (
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {Object.keys(TYPE_CONFIG).filter(key => selectedTypes.includes(key))
            .map(key => <SingleChart key={key} typeKey={key} data={data} total={totals[key]} />)}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '100px', color: '#666', background: '#fff', borderRadius: '10px' }}>
          {loading ? "데이터를 불러오는 중입니다..." : "선택한 조건의 데이터가 없습니다."}
        </div>
      )}
    </div>
  );
}

// ==========================================================
// 메인 컴포넌트
// ==========================================================

function UsageAnalysis({ API_BASE, tenantId, roomList = [] }) {
  const [subTab, setSubTab] = useState('all');

  const tabStyle = (active) => ({
    flex: 1, padding: '10px', border: 'none', borderRadius: '10px',
    fontWeight: 'bold', fontSize: '13px', cursor: 'pointer',
    backgroundColor: active ? '#4f46e5' : '#fff',
    color: active ? '#fff' : '#374151',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  });

  return (
    <div>
      {/* 서브탭 */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', padding: '0 4px' }}>
        <button style={tabStyle(subTab === 'all')}        onClick={() => setSubTab('all')}>📊 전체</button>
        <button style={tabStyle(subTab === 'individual')} onClick={() => setSubTab('individual')}>🏠 개별</button>
      </div>

      {subTab === 'all'        && <AllRoomsChart    API_BASE={API_BASE} tenantId={tenantId} />}
      {subTab === 'individual' && <IndividualChart  API_BASE={API_BASE} tenantId={tenantId} roomList={roomList} />}
    </div>
  );
}

export default UsageAnalysis;
